import React, { useEffect, useMemo, useRef, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useDrag } from "react-dnd";
import styled from "styled-components";
import { TASK_TITLE_CHAR_LIMIT } from "@lifeat/shared/task-management/TaskManagementConstants";
import { isTaskOverdue } from "@lifeat/shared/task-management/TaskManagementUtil";
import { TaskStatus } from "@lifeat/shared/task-management/TaskStatus";
import { DEFAULT_TAG_PALETTE, TAG_PALETTES, UNASSIGNED_TAG } from "@lifeat/shared/task-management/TaskTagConstants";
import { userHasTaskTimerAccess } from "@lifeat/shared/user/UserUtil";
import { formatISODateTimeString, getDateFromISODateString, getDisplayDateString } from "@lifeat/shared/util/DateUtil";
import { ContextMenuComponents } from "@common/LifeAtContextMenu/ContextMenuComponents";
import { DropdownMenuComponents } from "@common/LifeAtDropdownMenuV2/DropdownMenuComponents";
import LifeAtMultiLineTextInput from "@common/LifeAtTextInput/LifeAtMultiLineTextInput";
import { useDebouncedInput } from "@common/LifeAtTextInput/useDebouncedInput";
import { useAppearanceContext } from "@contexts/AppearanceContext";
import { useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import { useEvent } from "@hooks/useEvent";
import ClockIcon from "@icons/ClockIcon.svg";
import DragIcon from "@icons/DragIndicatorIcon.svg";
import EmptyCalendarIcon from "@icons/EmptyCalendarIcon.svg";
import MoreActionsIcon from "@icons/Menu-TripleVerticalDots-Icon.svg";
import PlayIcon from "@icons/PlayIcon.svg";
import RecurringIcon from "@icons/RecurringIcon.svg";
import { FontSize } from "@themes/Fonts";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import buildCompositeKey from "@util/ReactUtil";
import { blendHexColors } from "@util/StyleUtil";
import { DEFAULT_TASK_TIMER_MINUTES } from "../../../features/widget-workspace/components/Widget/TimerWidget/TimerWidgetConstants";
import { ItemTypes } from "../../drag-and-drop/DragAndDropConstants";
import { TaskDragItem } from "../../drag-and-drop/DragAndDropTypes";
import { projectList$ } from "../../projects/ProjectGlobals";
import { draggedTaskEvent$ } from "../TaskManagementGlobals";
import { ClientRecurringTask, ClientTaskItemType } from "../TaskManagementTypes";
import { startTaskTimer } from "../TaskTimerGlobals";
import { useTagAssignmentInput } from "../planner/useTagAssignmentInput";
import ProjectChip from "./ProjectChip";
import TagComponent from "./TagComponent";
import { StyledTaskTitleWrapper, TaskItemContainer, TaskStatusCheckbox } from "./TaskItemComponents";
import {
    TASK_TITLE_INPUT_DEBOUNCE_DELAY_MS,
    TASK_TITLE_INPUT_MAX_HEIGHT,
    TASK_TITLE_INPUT_MIN_HEIGHT,
    TASK_TITLE_INPUT_PLACEHOLDER,
} from "./TaskItemConstants";
import { AddTagDropdownTrigger, TagAssignmentDropdownContent } from "./TaskTagAssignmentDropdownComponents";
import { DropdownItem } from "./TaskTagDropdownComponents";
import TaskToTimerInfoModal from "./TaskToTimerInfoModal";

const TaskElementColumn = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
    gap: 8px;
`;

const TaskElementRow = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
`;

const TaskDetailsRow = styled.div`
    display: flex;
    flex-direction: row;

    width: 100%;
    justify-content: space-between;
    align-items: end;
`;

const TaskDetailsInfo = styled.div<{ wrapContent?: boolean }>`
    display: flex;
    flex-direction: ${(props) => (props.wrapContent ? "column" : "row")};
    gap: ${(props) => (props.wrapContent ? "3px" : "10px")};
`;

const TaskDetailsRowComponent = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
`;

const TimeDetailsRow = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
`;

const TimeDetails = styled.div<{ isOverdue: boolean }>`
    display: flex;
    align-items: center;
    padding: 2px 0;

    gap: 2px;
    color: ${(props) => (props.isOverdue ? props.theme.textColorSpecial : props.theme.textColorSecondary)};
    font-size: ${FontSize.small};
    white-space: nowrap;
`;

const StyledMoreActionsIcon = styled(MoreActionsIcon)`
    fill: ${(props) => props.theme.iconColor};
    cursor: pointer;
    transform: rotate(90deg);
`;

const StyledDragIcon = styled(DragIcon)`
    fill: ${(props) => props.theme.iconColor};
    cursor: pointer;
    position: absolute;
    top: 10px;
    left: 4px;

    visibility: hidden;
`;

const StyledCalendarIcon = styled(EmptyCalendarIcon)<{ isOverdue: boolean }>`
    fill: ${(props) => (props.isOverdue ? props.theme.textColorSpecial : props.theme.textColorSecondary)};
    cursor: pointer;
`;

const StyledClockIcon = styled(ClockIcon)<{ isOverdue: boolean }>`
    stroke: ${(props) => (props.isOverdue ? props.theme.textColorSpecial : props.theme.textColorSecondary)};
`;

const StyledRecurringIcon = styled(RecurringIcon)`
    stroke: ${(props) => (props.isOverdue ? props.theme.textColorSpecial : props.theme.textColorSecondary)};
`;

const StyledMoreActionsDropdownTrigger = styled(DropdownMenuComponents.Trigger)`
    display: flex;
    padding: 3px 8px;
    visibility: hidden;
`;

const StyledDropdownTrigger = styled(DropdownMenuComponents.Trigger)<{ isVisible: boolean }>`
    max-height: ${(props) => (props.isVisible ? `30px` : `0px`)};
    overflow: auto;
`;

const StyledPlayIcon = styled(PlayIcon)<{ isDisabled: boolean }>`
    fill: ${(props) => props.theme.iconColor};
    opacity: ${(props) => (props.isDisabled ? "0.3" : "1")};

    &:hover {
        opacity: 0.5;
    }

    width: 20px;
    height: 20px;
`;

const StyledProjectChip = styled(ProjectChip)``;

const TaskItemWrapper = styled.div`
    display: flex;
    width: 100%;
    gap: 5px;
    position: relative;

    &:hover {
        ${StyledDragIcon}, ${StyledMoreActionsDropdownTrigger} {
            visibility: visible;
        }

        ${StyledDropdownTrigger} {
            max-height: 30px;
        }
    }
`;

const StyledContextMenuTrigger = styled(ContextMenuComponents.Trigger)`
    width: 100%;
`;

export type TaskItemProps = {
    task: ClientTaskItemType | ClientRecurringTask;
    isTaskInFocus: boolean;
    recurIcon: boolean;
    taskMethods: {
        buildKey: () => string;
        getDndItem: () => TaskDragItem;
        updateTaskTitle: (title: string) => Promise<void>;
        updateTaskTag: (tag: string) => Promise<void>;
        updateTaskStatus: (status: TaskStatus) => Promise<void>;
    };
    events: {
        onClick: () => void;
        onFocus: () => void;
        onKeyPress: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
        onDrag: () => void;
        onComplete: () => void;
        onTagAssign: (tagId: string) => void;
        onTagRemove: () => void;
        onTimerStart: (duration: number) => void;
    };
    contextMenu: {
        key: string;
        label: string;
        onClick: () => void;
    }[];
};
export default function TaskItem(props: TaskItemProps) {
    const { lightMode } = useAppearanceContext();
    const { user, userData } = useUserContext();
    const { setUpgradeCtaContext } = useCallToActionContext();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const [isTagPopoverOpen, setIsTagPopoverOpen] = useState(false);

    const [isTaskTimerInfoModalOpen, setIsTaskTimerInfoModalOpen] = useState(false);

    useEffect(() => {
        if (props.isTaskInFocus && inputRef?.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(inputRef.current.value.length || 0, inputRef.current.value.length || 0);
        }
    }, [props.isTaskInFocus, inputRef?.current]);

    const item = useEvent(props.taskMethods.getDndItem);

    const [collected, drag, preview] = useDrag(() => ({
        type: ItemTypes.TASK,
        item,
        collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
        end: () => {
            draggedTaskEvent$.value = null;
        },
        options: {
            dropEffect: "move",
        },
    }));

    const handleDrag = useEvent(props.events.onDrag);

    useEffect(() => {
        if (collected.isDragging) {
            handleDrag();
        }
    }, [handleDrag, collected.isDragging]);

    const [selectedTagId, setSelectedTagId, selectedTag] = useTagAssignmentInput({
        taskTagId: props.task.tagId,
        updateTaskTag: props.taskMethods.updateTaskTag,
        onAssign: props.events.onTagAssign,
        onRemove: props.events.onTagRemove,
    });

    const isTaskCompleted = props.task.status === "COMPLETE";
    const schedulingDetails = props.task.schedulingDetails;
    const timeDetails = () => {
        if (
            schedulingDetails?.scheduleTime ||
            schedulingDetails?.scheduleDate ||
            schedulingDetails?.durationMins ||
            schedulingDetails?.dueDate
        ) {
            const isOverdue = isTaskOverdue(schedulingDetails, props.task.status);

            let dateStr: string;
            if (schedulingDetails?.scheduleTime) {
                dateStr = formatISODateTimeString(schedulingDetails.scheduleTime);
            } else if (schedulingDetails?.scheduleDate) {
                dateStr = getDisplayDateString(getDateFromISODateString(schedulingDetails.scheduleDate));
            }

            const dueDate = schedulingDetails?.dueDate
                ? getDateFromISODateString(schedulingDetails.dueDate)
                : undefined;

            return (
                <TimeDetailsRow>
                    <TimeDetails isOverdue={isOverdue} className={`time-details`}>
                        {props.recurIcon && <StyledRecurringIcon />}
                        {dateStr && <StyledCalendarIcon isOverdue={isOverdue} />}
                        {dateStr && <span>{dateStr} </span>}
                    </TimeDetails>
                    <TimeDetails isOverdue={isOverdue} className={`time-details`}>
                        {dueDate && <StyledClockIcon isOverdue={isOverdue} />}
                        {dueDate && <span>Due {getDisplayDateString(dueDate)} </span>}
                    </TimeDetails>
                </TimeDetailsRow>
            );
        } else {
            return null;
        }
    };

    const [inputVal, setInputValAndDebounce] = useDebouncedInput(
        props.task.title,
        props.taskMethods.updateTaskTitle,
        TASK_TITLE_INPUT_DEBOUNCE_DELAY_MS,
    );

    const taskActionDropdownItems = (
        <>
            {props.contextMenu.map((item) => (
                <DropdownItem key={item.key} onClick={item.onClick}>
                    {item.label}
                </DropdownItem>
            ))}
        </>
    );

    const taskActionsMenu = (
        <DropdownMenuComponents.Root>
            <StyledMoreActionsDropdownTrigger onClick={(e) => e.stopPropagation()}>
                <StyledMoreActionsIcon />
            </StyledMoreActionsDropdownTrigger>
            <DropdownMenuComponents.Content>{taskActionDropdownItems}</DropdownMenuComponents.Content>
        </DropdownMenuComponents.Root>
    );

    const startTask = () => {
        if (!userHasTaskTimerAccess(userData, user?.metadata?.creationTime)) {
            setUpgradeCtaContext("taskTimer", UpgradeCTASource.TASK_TIMER);
        } else if (!schedulingDetails?.durationMins) {
            setIsTaskTimerInfoModalOpen(true);
        } else {
            const timerDuration = props.task.schedulingDetails?.durationMins ?? DEFAULT_TASK_TIMER_MINUTES;
            startTaskTimer(props.task.schedulingDetails?.durationMins, props.task.title, () =>
                props.events.onTimerStart(timerDuration),
            );
        }
    };

    const taskProject = useMemo(
        () =>
            props.task.projectId && projectList$.value[props.task.projectId]
                ? projectList$.value[props.task.projectId]
                : undefined,
        [props.task.projectId, projectList$.value],
    );

    const palette = TAG_PALETTES.find((c) => c.primary === taskProject?.color) ?? DEFAULT_TAG_PALETTE;
    const projectBackgroundColor = lightMode ? palette.lighter : palette.darker;
    const projectRibbonColor = taskProject ? blendHexColors(projectBackgroundColor, palette.primary, 0.8) : undefined;

    const taskContainsTagAndProject = selectedTag !== UNASSIGNED_TAG && taskProject;
    const taskContainsScheduledDateAndDueDate =
        schedulingDetails?.dueDate && (schedulingDetails?.scheduleDate || schedulingDetails?.scheduleTime);

    const isTaskTimerDisabled =
        !schedulingDetails?.durationMins || !userHasTaskTimerAccess(userData, user?.metadata?.creationTime);

    return (
        <>
            <TaskItemWrapper ref={drag} key={`task-item-${props.taskMethods.buildKey()}`}>
                {!(isMobile || isTablet) && <StyledDragIcon />}
                <ContextMenuComponents.Root>
                    <StyledContextMenuTrigger>
                        <TaskItemContainer
                            onClick={props.events.onClick}
                            isTouchDevice={isMobile || isTablet}
                            ref={preview}
                            ribbonColor={projectRibbonColor}
                        >
                            <TaskElementColumn>
                                <TaskElementRow>
                                    <TaskStatusCheckbox
                                        key={buildCompositeKey(
                                            `task-status-checkbox`,
                                            props.taskMethods.buildKey(),
                                            isTaskCompleted,
                                        )}
                                        isTaskCompleted={isTaskCompleted}
                                        updateTaskStatus={props.taskMethods.updateTaskStatus}
                                        onComplete={props.events.onComplete}
                                    />
                                    <StyledTaskTitleWrapper isCompleted={isTaskCompleted}>
                                        <LifeAtMultiLineTextInput
                                            textValue={inputVal}
                                            placeholder={TASK_TITLE_INPUT_PLACEHOLDER}
                                            onInputChange={setInputValAndDebounce}
                                            minHeightPx={TASK_TITLE_INPUT_MIN_HEIGHT}
                                            maxHeightPx={TASK_TITLE_INPUT_MAX_HEIGHT}
                                            handleKeyPress={props.events.onKeyPress}
                                            maxCharacterCount={TASK_TITLE_CHAR_LIMIT}
                                            inputRef={inputRef}
                                            onFocus={props.events.onFocus}
                                        />
                                    </StyledTaskTitleWrapper>
                                    {taskActionsMenu}
                                </TaskElementRow>

                                <TaskDetailsRow>
                                    {/*Tag Assignment Dropdown*/}
                                    <DropdownMenuComponents.Root
                                        open={isTagPopoverOpen}
                                        onOpenChange={(open: boolean) => setIsTagPopoverOpen(open)}
                                    >
                                        <TaskDetailsInfo
                                            wrapContent={
                                                !!(taskContainsTagAndProject || taskContainsScheduledDateAndDueDate)
                                            }
                                        >
                                            {(selectedTag !== UNASSIGNED_TAG || taskProject) && (
                                                <TaskDetailsRowComponent>
                                                    {/*Update tag dropdown*/}
                                                    {selectedTag !== UNASSIGNED_TAG && (
                                                        <StyledDropdownTrigger
                                                            onClick={(e) => e.stopPropagation()}
                                                            isVisible={true}
                                                        >
                                                            <TagComponent tag={selectedTag} />
                                                        </StyledDropdownTrigger>
                                                    )}

                                                    {taskProject && (
                                                        <StyledProjectChip project={taskProject} disableHover={true} />
                                                    )}
                                                </TaskDetailsRowComponent>
                                            )}
                                            <TaskDetailsRowComponent>
                                                {timeDetails()}
                                                {/*Add tag dropdown*/}
                                                {selectedTag === UNASSIGNED_TAG && (
                                                    <StyledDropdownTrigger
                                                        onClick={(e) => e.stopPropagation()}
                                                        isVisible={isMobile || isTablet}
                                                    >
                                                        <AddTagDropdownTrigger />
                                                    </StyledDropdownTrigger>
                                                )}

                                                <DropdownMenuComponents.Content>
                                                    <TagAssignmentDropdownContent
                                                        selectedTagId={selectedTagId}
                                                        handleTagUpdate={(tagId: string) => {
                                                            setSelectedTagId(tagId);
                                                            setIsTagPopoverOpen(false);
                                                        }}
                                                    />
                                                </DropdownMenuComponents.Content>
                                            </TaskDetailsRowComponent>
                                        </TaskDetailsInfo>
                                    </DropdownMenuComponents.Root>
                                    <StyledPlayIcon
                                        isDisabled={isTaskTimerDisabled}
                                        onClick={(e: MouseEvent) => {
                                            e.stopPropagation();
                                            startTask();
                                        }}
                                    />
                                </TaskDetailsRow>
                            </TaskElementColumn>
                        </TaskItemContainer>
                    </StyledContextMenuTrigger>
                    <ContextMenuComponents.Content>{taskActionDropdownItems}</ContextMenuComponents.Content>
                </ContextMenuComponents.Root>
            </TaskItemWrapper>
            <TaskToTimerInfoModal
                isOpen={isTaskTimerInfoModalOpen}
                handleCloseModal={() => setIsTaskTimerInfoModalOpen(false)}
            />
        </>
    );
}
